import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { Size } from "../../types";
import { Box, Button, Link } from "../../common/components";
import { Typography } from "@mui/material";
import { createStyles, palette, spacing } from "../../styles/theme";
import SizeInput from "../Sizes/SizeInput";

const styles = createStyles({
  container: {
    overflowX: "auto",
  },
  grid: {
    display: "grid",
    py: "8px",
    gap: "8px",
    columnGap: {
      xs: spacing.smallMobile,
      lg: spacing.smallDesktop,
    },
    rowGap: "24px",
  },
  input: {
    width: "56px",
    p: "8px 12px",
    textAlign: "center",
    minHeight: "40px",
  },
});

interface Props {
  onUpdateSizesQty?: (sizes: Size[]) => Promise<void>;
  sizes: Size[];
  staticVersion?: boolean;
}

const OrderItemSizes = (props: Props) => {
  const { onUpdateSizesQty = null, sizes, staticVersion = false } = props;

  const [isEditing, setEditing] = useState(false);
  const [sizesState, setSizesState] = useState<Size[]>([]);

  //Initial loading
  useEffect(() => {
    setSizesState(sizes);
  }, [sizes, setSizesState]);

  const qty = useMemo(() => {
    return sizesState.reduce((acc, curr) => {
      const parsedQty = parseInt(curr.qty?.toString() || "0");
      return acc + (isNaN(parsedQty) ? 0 : parsedQty);
    }, 0);
  }, [sizesState]);

  const { t } = useTranslation();

  const onClickEditSizesQty = useCallback(() => {
    setEditing(true);
  }, [setEditing]);

  const onClickSaveSizesQty = useCallback(() => {
    onUpdateSizesQty?.(sizesState).then(() => {
      setEditing(false);
    });
  }, [setEditing, onUpdateSizesQty, sizesState]);

  const onClickCancelEdit = useCallback(() => {
    //Reset to original values
    setSizesState(sizes);

    setEditing(false);
  }, [setEditing, setSizesState, sizes]);

  const onSizeChange = useCallback(
    (sizeName: string, sizeQty: string) => {
      setSizesState((oldSizes) => {
        const newSizes = _.cloneDeep(oldSizes);

        const sizeToUpdate = newSizes.find((size) => size.name === sizeName);
        if (sizeToUpdate) {
          sizeToUpdate.qty = parseInt(sizeQty);
        }

        return newSizes;
      });
    },
    [setSizesState]
  );

  return (
    <Box sx={styles.container}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb="16px"
      >
        <Typography variant="textRegularBold" color={palette.grey.nearBlack}>
          Total {qty} {t("order.units")}
        </Typography>
        <Box sx={{ displayPrint: "none" }}>
          {staticVersion || !onUpdateSizesQty ? null : !isEditing ? (
            <Link onClick={onClickEditSizesQty}>{t("order.edit_sizes")}</Link>
          ) : (
            <Box display="flex" gap="8px" alignItems="center">
              <Button onClick={onClickSaveSizesQty} size="small">
                {t("general.save")}
              </Button>
              <Button
                onClick={onClickCancelEdit}
                size="small"
                variant="outlined"
                sx={{ maxHeight: "32px" }}
              >
                {t("general.cancel")}
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          ...styles.grid,
          maxWidth: "99%",
          gridTemplateColumns: `repeat(auto-fill, minmax(48px, 1fr))`,
        }}
      >
        {sizesState?.map((size) => {
          return (
            <Box
              key={size.name}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "8px",
                minWidth: "fit-content",
              }}
            >
              {isEditing ? (
                <SizeInput
                  key={`${size.name}`}
                  itemSize={size}
                  onSizeChange={(e) => onSizeChange(size.name, e.target.value)}
                />
              ) : (
                <>
                  <Typography
                    variant="textRegularBold"
                    color={palette.grey.nearBlack}
                    whiteSpace="nowrap"
                  >
                    {t(`sizes.${size.name.toUpperCase()}`)}
                  </Typography>
                  <Typography
                    variant="actionBigIcon"
                    color={palette.grey.nearBlack}
                  >
                    {size.qty}
                  </Typography>
                </>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default OrderItemSizes;
